import { CheckField, ClassStudent, ClassStudentResponse } from '../model';
import { Nullable } from '../types';

export function takeClassStudent(
  arr: Nullable<ClassStudent>[]
): Nullable<ClassStudent>[] {
  return arr.map(({ pid, sid, grade, no, seat }) => ({
    pid,
    sid,
    grade,
    no,
    seat,
  }));
}

export function isAllHavePid(arr: ClassStudent[]): boolean {
  return !arr.some(({ pid }) => !pid);
}

export function isAllHaveSid(arr: ClassStudent[]): boolean {
  return !arr.some(({ sid }) => !sid);
}

export function isAllHaveGradeNoSeat(arr: ClassStudent[]): boolean {
  return !arr.some(({ grade, no, seat }) => !grade || !no || !seat);
}

export function CheckShouldUseIdentify<T extends ClassStudent>(
  students: T[]
): [CheckField, (f: ClassStudentResponse, t: T) => boolean] {
  if (isAllHavePid(students)) {
    return [CheckField.Pid, (f, t) => f.pid === t.pid];
  }
  if (isAllHaveSid(students)) {
    return [CheckField.Sid, (f, t) => f.sid === t.sid];
  }
  if (isAllHaveGradeNoSeat(students)) {
    return [
      CheckField.GradeNoSeat,
      (f, t) => f.grade === t.grade && f.no === t.no && f.seat === t.seat,
    ];
  }
  return [CheckField.None, () => false];
}

export function checkDuplicates(data: any[]): {
  duplicatePids: string[];
  duplicateSids: string[];
  duplicateGradeGroups: string[];
} {
  const pidCount: Record<string, number> = {};
  const sidCount: Record<string, number> = {};
  const gradeGroupCount: Record<string, number> = {};

  data.forEach((item) => {
    if (typeof item !== 'object' || !item) return;

    // 統計 pid
    if (item.pid && typeof item.pid === 'string') {
      pidCount[item.pid] = (pidCount[item.pid] || 0) + 1;
    }

    // 統計 sid（忽略空值）
    if (item.sid && typeof item.sid === 'string' && item.sid.trim() !== '') {
      sidCount[item.sid] = (sidCount[item.sid] || 0) + 1;
    }

    if (item.grade && item.no && item.seat) {
      // 生成唯一 key
      // 確保 no 和 seat 至少為兩位數，不足則補零
      const formattedNo =
        item.no != null ? String(item.no).padStart(2, '0') : '00';
      const formattedSeat =
        item.seat != null ? String(item.seat).padStart(2, '0') : '00';
      const gradeGroupKey = `${
        item.grade ?? 'null'
      }${formattedNo}${formattedSeat}`;
      gradeGroupCount[gradeGroupKey] =
        (gradeGroupCount[gradeGroupKey] || 0) + 1;
    }
  });

  // 找出重複的 pid
  const duplicatePids = Object.keys(pidCount).filter(
    (pid) => pidCount[pid] > 1
  );

  // 找出重複的 sid
  const duplicateSids = Object.keys(sidCount).filter(
    (sid) => sidCount[sid] > 1
  );

  // 找出重複的 (grade, no, seat) 組合
  const duplicateGradeGroups = Object.keys(gradeGroupCount).filter(
    (key) => gradeGroupCount[key] > 1 && key !== 'null0000'
  );

  return {
    duplicatePids,
    duplicateSids,
    duplicateGradeGroups,
  };
}

export function fieldIssueWording(fieldIssue: CheckField): string {
  switch (fieldIssue) {
    case CheckField.Pid:
      return '身份證不正確';
    case CheckField.Sid:
      return '學號不正確';
    case CheckField.GradeNoSeat:
      return '年級班級座號不正確';
    default:
      throw new Error('check field is invalid');
  }
}

type RemarkObj = ClassStudent & { remark: string; okUpload: boolean;[K: string]: unknown };
// type ExistsObj = { exists: boolean; [K: string]: unknown };
export function assignExists<T extends RemarkObj>(
  matchFunc: (from: ClassStudentResponse, to: T) => boolean,
  datas: T[],
  exists: ClassStudentResponse[]
): T[] {
  datas.forEach((d) => {
    const e = exists.find((e) => matchFunc(e, d));
    if (!e?.exists) {
      d.okUpload = false;
      if (d.remark) {
        d.remark += `，該學生不存在`;
      } else {
        d.remark += `該學生不存在`;
      }
    } else {
      d.remark += e.fieldIssues.map((fi) => fieldIssueWording(fi)).join('，');
    }
  });
  return datas;
}

export function checkOkToUpload<T extends RemarkObj>(
  okUpload: boolean,
  data: T[]
): boolean {
  // console.log('checkOkToUpload: ',okUpload);
  if (okUpload) {
    const a = data.every(function (item) {
      return item.remark === '' && item.okUpload;
    });
    // console.log(a);
    return a;
  } else {
    return false;
  }
}

export function checkOkToUpdate<T extends RemarkObj>(
  okUpload: boolean,
  data: T[]
): boolean {
  if (okUpload) {
    const a = data.every(function (item) {
      return item.remark === '' && item.okUpload;
    });
    return a;
  } else {
    return false;
  }
}
